import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";

/**
 * Skickar en engångslänk för inloggning utan lösenord. Svaret är alltid
 * neutralt så att det inte går att kartlägga vilka konton som finns.
 */
export const requestMagicLink = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string; origin?: string; locale?: string }) => ({
    email: String(input?.email ?? "")
      .trim()
      .toLowerCase()
      .slice(0, 255),
    origin: String(input?.origin ?? ""),
    locale: (["sv", "en", "es"].includes(String(input?.locale)) ? String(input?.locale) : "sv") as
      | "sv"
      | "en"
      | "es",
  }))
  .handler(async ({ data }) => {
    if (!data.email || !data.email.includes("@")) {
      return { status: "ok" as const, seconds: 0 };
    }

    const ipRaw =
      getRequestHeader("cf-connecting-ip") ??
      getRequestHeader("x-forwarded-for") ??
      getRequestHeader("x-real-ip") ??
      "";
    const ip = ipRaw.split(",")[0]?.trim() ?? "";

    const { checkBlocked, registerFailure } = await import("@/lib/login-guard.server");
    const keys = [`magic:email:${data.email}`, ...(ip ? [`magic:ip:${ip}`] : [])];

    const blocked = await checkBlocked(keys);
    if (blocked > 0) return { status: "blocked" as const, seconds: blocked };
    // Varje begäran räknas, så att länkar inte kan spammas.
    await registerFailure({
      keys,
      email: data.email,
      ip,
      userAgent: getRequestHeader("user-agent") ?? "",
    });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: list } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 1000 });
    const user = (list?.users ?? []).find((u) => (u.email ?? "").toLowerCase() === data.email);
    if (!user) return { status: "ok" as const, seconds: 0 };

    const meta = (user.user_metadata ?? {}) as { deleted_at?: string | null };
    const banned = (user as { banned_until?: string | null }).banned_until ?? null;
    const isBlocked = !!banned && new Date(banned).getTime() > Date.now();
    if (isBlocked || meta.deleted_at) return { status: "ok" as const, seconds: 0 };

    const link = await supabaseAdmin.auth.admin.generateLink({
      type: "magiclink",
      email: data.email,
    });
    const tokenHash = link.data?.properties?.hashed_token;
    if (link.error || !tokenHash) return { status: "ok" as const, seconds: 0 };

    const { siteOrigin } = await import("@/lib/site-origin");
    const allowed = /^https:\/\/([a-z0-9-]+\.)*(sweintschool\.com|lovable\.app|lovableproject\.com)$/i;
    const base = allowed.test(data.origin) ? data.origin : siteOrigin();
    const url = `${base}/auth/callback?token_hash=${encodeURIComponent(tokenHash)}`;

    const copy = {
      sv: {
        subject: "Din inloggningslänk",
        heading: "Logga in utan lösenord",
        intro: "Klicka på knappen nedan för att logga in på Swedish International School.",
        button: "Logga in",
        footnote:
          "Länken gäller en gång och en kort stund. Har du inte begärt den kan du bortse från mejlet.",
      },
      en: {
        subject: "Your sign-in link",
        heading: "Sign in without a password",
        intro: "Click the button below to sign in to Swedish International School.",
        button: "Sign in",
        footnote:
          "The link works once and for a short time. If you did not request it, you can ignore this email.",
      },
      es: {
        subject: "Tu enlace de acceso",
        heading: "Acceder sin contraseña",
        intro: "Pulsa el botón para acceder a Swedish International School.",
        button: "Acceder",
        footnote:
          "El enlace funciona una vez y durante poco tiempo. Si no lo has solicitado, ignora este correo.",
      },
    }[data.locale];

    const { renderSystemEmail, sendSystemEmail } = await import("@/lib/mailer.server");
    const html = renderSystemEmail({
      locale: data.locale,
      heading: copy.heading,
      intro: copy.intro,
      buttonLabel: copy.button,
      buttonUrl: url,
      footnote: copy.footnote,
    });

    await sendSystemEmail({
      kind: "access_link",
      to: data.email,
      subject: copy.subject,
      html,
      text: `${copy.intro}\n\n${url}\n\n${copy.footnote}`,
      locale: data.locale,
      meta: { purpose: "magic_link" },
    });

    return { status: "ok" as const, seconds: 0 };
  });
